import React from 'react'
import { css, cx } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { Props as IntlProps } from 'react-intl/src/components/message'
import { LegacyEmptyState } from '../legacy-empty-state'
import { legacyTheme } from '../legacy-theme'
import { theme } from '../../styled'
import {
  Cell,
  LegacyGridSpinner,
  LegacyGridSortAction,
  ParentRow,
  SortConfig,
  SortOrder,
  TableRow,
  TableRows,
  LegacyGridColumn
} from './index'

export const bodyDataTestID = 'legacy-grid-body'
export const headDataTestID = 'legacy-grid-head'
export const emptyRowsMsgContainerDataTestID =
  'legacy-grid-empty-rows-msg-container'
export const columnsHeaderContainerDataTestID =
  'legacy-grid-columns-header-container'
export const columnSortingDataTestID = 'legacy-grid-column-sorting'

interface Props<TLegacyRowData> {
  readonly columnConfigs: Array<LegacyGridColumn<TLegacyRowData>>
  readonly rowData: TLegacyRowData[]
  readonly rowDataParents?: ParentRowData[]
  readonly loading: boolean
  readonly onSort: (sortConfig: SortConfig<TLegacyRowData>) => void
  readonly emptyGridMessageConfig: IntlProps
  readonly activeSortConfigs: Array<SortConfig<TLegacyRowData>>
  readonly 'data-testid'?: string
  readonly rowClassName?: string
}

export interface ParentRowData {
  readonly id: string
  readonly name: string
}

export interface BaseRowData {
  readonly id: string
  readonly parent?: ParentRowData
}

export function LegacyGrid<TLegacyRowData extends BaseRowData>({
  columnConfigs,
  rowData,
  rowDataParents,
  loading,
  onSort,
  emptyGridMessageConfig,
  activeSortConfigs,
  rowClassName,
  ...props
}: Props<TLegacyRowData>) {
  if (loading) {
    return (
      <LegacyGridSpinner
        dataTestID={`${props['data-testid']}-loading-indicator`}
      />
    )
  }

  return (
    <table
      data-testid={props['data-testid']}
      className={cx(
        'legacy-ds-grid',
        css`
          width: 100%;
          border-top: 1px solid ${legacyTheme.colors.tableBorder};
          border-bottom: 1px solid ${legacyTheme.colors.tableBorder};
          color: ${legacyTheme.colors.grayMiddle};
        `
      )}
    >
      <thead
        data-testid={headDataTestID}
        className={css`
          height: 31px;
          background-color: #fff;
        `}
      >
        <TableRow
          key={'column-header-table-row'}
          dataTestID={columnsHeaderContainerDataTestID}
        >
          {columnConfigs.map(
            (
              { heading, headingStyles, sorting, horizontalCenter },
              columnIndex
            ) => {
              const found = !!sorting
                ? activeSortConfigs.find(
                    (sortConfig) => sortConfig.field === sorting.field
                  )
                : undefined
              const activeSortOrder = !!found ? found.order : undefined
              const isLastColumn = columnIndex === columnConfigs.length - 1

              const extraStyles = !!headingStyles
                ? cx(
                    !!headingStyles.paddingLeft &&
                      css`
                        padding-left: ${headingStyles.paddingLeft};
                      `
                  )
                : undefined

              return (
                <Cell
                  key={columnIndex}
                  Component="th"
                  isLastColumn={isLastColumn}
                  className={cx(
                    css`
                      font-weight: ${theme.fontWeights.secondary.bold};
                      padding-left: 8px;
                    `,
                    extraStyles
                  )}
                >
                  <div
                    className={css`
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <div
                      className={css`
                        flex: 2;
                        ${horizontalCenter && 'text-align: center'};
                      `}
                    >
                      {!!heading && <FormattedMessage {...heading} />}
                    </div>
                    {!!sorting && (
                      <div
                        data-testid={columnSortingDataTestID}
                        className={css`
                          flex: 1;
                          align-self: flex-end;
                          text-align: right;
                        `}
                      >
                        <LegacyGridSortAction
                          activeSortOrder={activeSortOrder}
                          onSortAscending={() =>
                            onSort({
                              ...sorting,
                              order: SortOrder.ASC
                            })
                          }
                          onSortDescending={() =>
                            onSort({
                              ...sorting,
                              order: SortOrder.DESC
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </Cell>
              )
            }
          )}
        </TableRow>
      </thead>
      <tbody
        data-testid={bodyDataTestID}
        data-e2e={bodyDataTestID}
        className={cx(
          'legacy-grid-rows',
          css`
            width: 100%;
          `
        )}
      >
        {rowData.length === 0 && (
          <TableRow
            className={rowClassName}
            isFirstRow={true}
            isLastRow={true}
            dataTestID={emptyRowsMsgContainerDataTestID}
          >
            <td colSpan={columnConfigs.length}>
              <LegacyEmptyState
                children={<FormattedMessage {...emptyGridMessageConfig} />}
              />
            </td>
          </TableRow>
        )}

        {Array.isArray(rowDataParents) ? (
          rowDataParents.map((parent, index) => {
            const filteredRowData = filterRowDataByRowParent<TLegacyRowData>(
              rowData,
              parent
            )
            const isParentOfMultipleRows = filteredRowData.length > 1

            const isEven = index % 2 === 0

            const rowBackground = isEven
              ? css`
                  background-color: ${legacyTheme.colors.grayLightest};
                `
              : undefined

            return (
              <>
                {isParentOfMultipleRows && (
                  <ParentRow
                    key={`${index}-parent-row`}
                    name={parent.name}
                    columnConfigLength={columnConfigs.length}
                    rowClassName={rowBackground}
                  />
                )}
                <TableRows<TLegacyRowData>
                  key={`${index}-table-rows`}
                  rowsData={filteredRowData}
                  columnConfigs={columnConfigs}
                  rowClassName={cx(rowClassName, rowBackground)}
                  areRowsInParent={isParentOfMultipleRows}
                />
              </>
            )
          })
        ) : (
          <TableRows<TLegacyRowData>
            rowsData={rowData}
            columnConfigs={columnConfigs}
            rowClassName={cx(
              rowClassName,
              css`
                &:nth-of-type(even) {
                  background-color: ${legacyTheme.colors.grayLightest};
                }
              `
            )}
          />
        )}
      </tbody>
    </table>
  )
}

export function filterRowDataByRowParent<TLegacyRowData extends BaseRowData>(
  rowData: TLegacyRowData[],
  parent: ParentRowData
): TLegacyRowData[] {
  return rowData.filter((row) => {
    return !!row.parent && row.parent.id === parent.id
  })
}
